import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, DEFAULT_OG_IMAGE, buildCanonicalUrl } from '@/lib/seo';

// Helmet stamps data-rh on every tag it manages and the prerenderer stamps
// nothing, so :not([data-rh]) is what distinguishes a prerendered static tag
// from a live one. Keying off it also means page components that inject their
// own JSON-LD through Helmet are never touched by the cleanup below.
const STATIC_JSON_LD_SELECTOR = 'head script[type="application/ld+json"]:not([data-rh])';

// The prerendered static HTML ships its own meta/canonical set for non-JS
// crawlers. Once Helmet mounts it emits the same tags (marked data-rh), so the
// static copies must be removed or the rendered DOM carries duplicates — and a
// duplicate rel=canonical makes Google ignore both.
function removeStaticSeoTags() {
  const selectors = [
    'head meta[name="description"]:not([data-rh])',
    'head meta[name="keywords"]:not([data-rh])',
    'head link[rel="canonical"]:not([data-rh])',
    'head meta[property^="og:"]:not([data-rh])',
    'head meta[name^="twitter:"]:not([data-rh])',
  ];
  document.querySelectorAll(selectors.join(', ')).forEach((el) => el.remove());
}

// JSON-LD is the one set the prerenderer OWNS rather than duplicates, so it is
// resolved the opposite way to the tags above: the static @graph stays and
// Helmet's competing blocks are suppressed. It has to win, because it is the
// only JSON-LD on every page that passes no structuredData at all — dropping it
// would leave the homepage's Organization/WebSite/SoftwareApplication/Product/
// VideoObject graph, and /use-cases, /blog, /download, /privacy and /terms,
// with no structured data whatsoever once JS runs. Keeping it also makes the
// JS and no-JS crawler paths serve byte-identical structured data instead of
// two sets that disagree on breadcrumb naming.
//
// It is only authoritative for the URL it was prerendered for, though. After a
// client-side route change the block still sitting in <head> describes the
// previously loaded page, so it is matched against this page's canonical and
// discarded when it does not describe it — Helmet's per-route JSON-LD then
// takes over rather than the DOM advertising another page's schema.
function hasAuthoritativeStaticJsonLd(canonicalUrl: string): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.querySelector(STATIC_JSON_LD_SELECTOR);
  if (!el) return false;
  try {
    const parsed = JSON.parse(el.textContent || '') as {
      '@graph'?: Array<{ '@type'?: string; url?: string }>;
    };
    const nodes = parsed['@graph'] ?? [parsed as { '@type'?: string; url?: string }];
    const page = nodes.find((node) => node['@type'] === 'WebPage' || node['@type'] === 'Article');
    return page?.url === canonicalUrl;
  } catch {
    // Unparseable static JSON-LD is worse than none: fall through to Helmet's.
    return false;
  }
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: 'website' | 'article';
  structuredData?: object | object[];
  noIndex?: boolean;
}

export function SEOHead({
  title,
  description,
  canonical,
  keywords,
  ogImage,
  ogImageAlt,
  ogType = 'website',
  structuredData,
  noIndex = false,
}: SEOHeadProps) {
  const fullCanonical = buildCanonicalUrl(canonical);
  const image = ogImage || DEFAULT_OG_IMAGE;

  // Resolved from the current canonical so a client-side navigation between two
  // routes rendered by the same page component re-decides instead of inheriting
  // the first route's answer.
  const staticJsonLdIsAuthoritative = useMemo(
    () => hasAuthoritativeStaticJsonLd(fullCanonical),
    [fullCanonical]
  );

  useEffect(() => {
    removeStaticSeoTags();
    if (!staticJsonLdIsAuthoritative) {
      document.querySelectorAll(STATIC_JSON_LD_SELECTOR).forEach((el) => el.remove());
    }
  }, [staticJsonLdIsAuthoritative, fullCanonical]);

  // Support both single schema and array of schemas. Suppressed entirely when
  // the prerendered @graph already describes this URL — emitting these on top of
  // it put two BreadcrumbLists with different names in one DOM, which is
  // contradictory structured data and costs rich-result eligibility.
  const schemas =
    staticJsonLdIsAuthoritative || !structuredData
      ? []
      : Array.isArray(structuredData)
        ? structuredData
        : [structuredData];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullCanonical} />

      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={image} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}

      {/* Structured Data */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
